import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars

import {Page} from '../../../common/components';
import {Gif} from '../../components/Gif';
import {subscribe, transformProps} from '../../../../replete';
import {UploadView} from './views/UploadView';
import lazyLoadComponent from '../../../common/components/lazyLoadComponent';
import {Metadata} from '../../../common/metadata';
import {getFileExtension, isValidFileType} from '../../../common/util/files';

import {ITEM_STATES, SETTINGS} from './constants';
import {createMediaFormatsList} from './util';
import UploadGifDataObject from './components/UploadGifDataObject';

const TaggingView = lazyLoadComponent(() => import('./views/TaggingView'), 'TaggingView', -1);
const ImageViewer = lazyLoadComponent(() => import('./views/ImageViewer'), 'ImageViewer', -1);

type PageError = {
    type: string;
    message: string;
};

@subscribe({
    auth: ['api.auth'],
})
@transformProps((props: jsx.Props, context: jsx.AppGlobals) => {
    props.authedUsername = context.authService.getUsername();

    return props;
})
@transformProps((props: jsx.Props) => {
    props.pid = new URLSearchParams(props.location.search).get('caption_item_id');

    return props;
})
@subscribe({
    queueIdx: ['ui.GifMakerPage.queueIdx'],
    view: ['ui.GifMakerPage.view'],
    itemPageCaptioning: ['ui.GifMakerPage.itemPageCaptioning'],
    gifToCaption: ['api.gifs.byId.*', 'pid'],
})
export class GifMakerPage extends Page {
    static debugNamespace = 'GifMakerPage';

    newItemsForQueue?: UploadGifDataObject[];
    queuePromises?: Promise<void>[];
    allowedFileTypes: string[];

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        this.state = this.defaultState();
        this.allowedFileTypes = SETTINGS.ALLOWED;
        this.newItemsForQueue = [];
    }

    pageInit(props: jsx.Props) {
        const gettextSub = this.context.gtService.gettextSub;

        this.title = gettextSub('GIF Maker | Tenor');
        this.description = gettextSub('Upload animated GIFs to Tenor to share on social media channels, text messages, and everywhere else.');

        this.context.store.set('ui.GifMakerPage.makerPage', this);

        this.state = this.defaultState();
        this.allowedFileTypes = SETTINGS.ALLOWED;
        this.newItemsForQueue = [];
    }
    propsNeedProcessing(nextProps: jsx.Props) {
        for (const key of ['itemPageCaptioning', 'gifToCaption']) {
            if (nextProps[key] !== this.props[key]) {
                return true;
            }
        }
        return false;
    }
    processNewProps(props: jsx.Props, state: jsx.State) {
        this.handleGifToCaption(props);

        if (props.itemPageCaptioning.status) {
            this.addItemPageCaptioningGifToQueue(props);
        }
    }

    handleGifToCaption(props: jsx.Props) {
        if (process.env.BROWSER && !props.itemPageCaptioning.status &&
            props.gifToCaption.results.length) {
            const gif = props.gifToCaption.results[0];
            if (gif && gif.media_formats) {
                const newVal = {
                    status: true,
                    gif,
                };
                this.context.store.set('ui.GifMakerPage.*', ['itemPageCaptioning'], newVal);
                const view = 'editing';
                this.context.store.set('ui.GifMakerPage.*', ['view'], view);
                props.itemPageCaptioning = newVal;
                props.view = view;
            }
            this.triggerUpdate();
        }
    }

    componentWillUnmount() {
        this.context.store.call('ui.GifMakerPage', 'reset');
    }

    defaultState() {
        const defaultState = {
            queue: [],
            pageErrors: [],
            pageErrorsUpdated: false,
            queueUpdated: false,
            updateCounter: 0,
        };
        return defaultState;
    }

    addItemPageCaptioningGifToQueue(props: jsx.Props) {
        const gif = props.itemPageCaptioning.gif;
        if (gif) {
            const url = Gif.getFormatUrlAuthed(gif, 'gif', this.context.apiService);
            this.addItemsToQueue([url]);
        }
    }

    @autobind
    addItemsToQueue(data) {
        this.resetPageErrors();
        const maxUploads = this.maxUploads();
        this.newItemsForQueue = [];
        this.queuePromises = [];
        this.debug(`FILE PICKER -- ${data.length} file(s)`, data);
        this.debug(`FILE PICKER -- ${this.state.queue?.length} queue`,
            this.state.queue,
        );

        for (let i = 0; (i < data.length) &&
            (i + this.state.queue.length < maxUploads); i++) {
            const datum = data[i];
            const obj = {url: undefined, file: undefined};
            if (typeof datum === 'string') {
                obj.url = datum;
            } else {
                obj.file = datum;
            }
            this.debug(`FILE PICKER -- FILE_${i}`, datum);
            if (isValidFileType(obj, this.allowedFileTypes)) {
                this.queuePromises.push(this.addToQueuePromise(obj));
            } else {
                this.updatePageErrors([{
                    type: 'filetype',
                    message: `Failed to load. Improper file type: ${
                        getFileExtension(obj)}.`,
                }]);
            }
        }
        Promise.all(this.queuePromises).then(() => {
            const queue = this.state.queue.concat(this.newItemsForQueue);
            let view;
            if (!queue.length) {
                view = 'upload';
            } else if (this.props.itemPageCaptioning.status) {
                view = 'editing';
            } else if (!this.props.itemPageCaptioning.status && this.props.view === 'upload') {
                window.scrollTo(0, 0);
                view = 'tagging';
                this.trackEvent({
                    eventName: 'tagging_page_revealed',
                    params: {
                        'category': createMediaFormatsList({
                            queue,
                            type: 'uploadObject',
                        }),
                    },
                });
            }

            this.setState({
                queue,
                queueUpdated: true,
            });
            view && this.context.store.set('ui.GifMakerPage.view', view);
        });
    }

    removeGifFromQueue(i: number) {
        const queue = this.state.queue.slice(0, i).concat(
            this.state.queue.slice(i + 1));
        const finishedUploading = queue.every(
            (uploadObject) => uploadObject.uploadStatus === ITEM_STATES.DONE);

        if (!queue.length) {
            this.setState({
                queue,
                queueUpdated: true,
            });
            this.context.store.set('ui.GifMakerPage.view', 'upload');
        } else if (finishedUploading) {
            this.redirect(this.linkToProfile());
        } else {
            this.setState({
                queue,
                queueUpdated: true,
            });
            this.context.store.set('ui.GifMakerPage.view', 'tagging');
        }
    }

    @autobind
    addToQueuePromise({url, file}): Promise<void> {
        const uploadObject = new UploadGifDataObject({
            url,
            file,
            context: this.context,
        });

        return new Promise<void>((resolve, reject) => {
            uploadObject.processData().then(() => {
                this.newItemsForQueue.push(uploadObject);
                resolve();
            }).catch((error) => {
                console.error(error);
                console.error(`${file ? file.name : url} failed to load`);
                this.updatePageErrors([{
                    type: 'processing',
                    message: error.message,
                }]);
                resolve();
            });
        });
    }

    goToProfile() {
        this.redirect(this.linkToProfile());
    }

    maxUploads() {
        const userFlags = this.context.authService.getUserFlags();
        if (userFlags.includes('admin')) {
            return 100;
        } else if (userFlags.includes('partner')) {
            return 50;
        } else {
            return 10;
        }
    }

    @autobind
    clearQueue() {
        this.setState(this.defaultState());
    }

    parsePageErrors(errorQueue: PageError[]): string[] {
        const gettextSub = this.context.gtService.gettextSub;
        if (!errorQueue.length) {
            return [];
        }
        const pageErrorMessages = [];
        let processingErrorsNum = 0;
        let filetypeErrorsNum = 0;
        let urlInputError = 0;
        let uploadErrorsNum = 0;
        let otherErrors = 0;

        errorQueue.forEach((error) => {
            if (error.type === 'upload') {
                uploadErrorsNum++;
            } else if (error.type === 'url-input') {
                urlInputError++;
            } else if (error.type === 'processing') {
                processingErrorsNum++;
            } else if (error.type === 'filetype') {
                filetypeErrorsNum++;
            } else {
                otherErrors++;
            }
        });

        if (uploadErrorsNum) {
            if (uploadErrorsNum === 1) {
                pageErrorMessages.push(gettextSub('There was an issue uploading {uploadErrorsNum} of your files. Please edit your upload and try again.', {uploadErrorsNum}));
            } else {
                pageErrorMessages.push(gettextSub('There was an issue uploading {uploadErrorsNum} of your files. Please edit your uploads and try again.', {uploadErrorsNum}));
            }
        }
        if (urlInputError) {
            pageErrorMessages.push(gettextSub('Please enter a valid URL.'));
        }
        if (processingErrorsNum) {
            pageErrorMessages.push(gettextSub('There was an issue processing {processingErrorsNum} of your files.', {processingErrorsNum}));
            errorQueue.forEach((error) => {
                pageErrorMessages.push(error.message);
            });
        }
        if (filetypeErrorsNum) {
            const multipleFiles = filetypeErrorsNum > 1;
            let msg;
            if (multipleFiles) {
                msg = gettextSub('{filetypeErrorsNum} of your uploads could not be processed since they are not GIFs, MP4s, PNGs, or JPEGs', {filetypeErrorsNum});
            } else {
                msg = gettextSub('{filetypeErrorsNum} of your uploads could not be processed since it is not a GIF, MP4, PNG or JPEG', {filetypeErrorsNum});
            }
            pageErrorMessages.push(msg);
        }
        if (otherErrors) {
            pageErrorMessages.push(gettextSub('Unknown Error: Please try again.'));
        }

        return pageErrorMessages;
    }
    updatePageErrors(errors: PageError[]) {
        const pageErrors = this.state.pageErrors.concat(errors);
        this.resetPageErrors(pageErrors);
    }
    resetPageErrors(pageErrors = []) {
        this.setState({
            pageErrors,
            pageErrorsUpdated: true,
        });
    }

    trackEvent({eventName, params = {}, gaParams = {}}) {
        if (this.props.pid) {
            params['rid'] = this.props.pid;
        }

        this.context.apiService.trackGifMakerEvent(
            {eventName, params, gaParams});
    }

    renderPage() {
        const {view} = this.props;
        const {
            queue,
            queueUpdated,
            pageErrors,
            pageErrorsUpdated,
        } = this.state!;
        return (
            <div className="GifMakerPage">
                <Metadata page={this}/>
                { view === 'upload' &&
                    <UploadView
                        queue={queue}
                        queueUpdated={queueUpdated}
                        pageErrors={pageErrors}
                        pageErrorsUpdated={pageErrorsUpdated}
                    />}
                { view === 'tagging' &&
                    <TaggingView
                        queue={queue}
                        queueUpdated={queueUpdated}
                        pageErrors={pageErrors}
                        pageErrorsUpdated={pageErrorsUpdated}
                    />}
                { (view === 'imageViewer' || view === 'editing') &&
                    <ImageViewer
                        queue={queue}
                    />}
            </div>
        );
    }
}
